import {
  Entity,
  Relationship,
  RelationshipDataBySource,
  RelationshipsData,
  RelationshipsEntityMap,
} from '../types/types';

type RelationshipsDataMap = {
  [key: string]: RelationshipsData[];
};

const isRelevantEntity = (
  entity: Entity,
  domain: string,
  type: string,
): boolean => entity.domain === domain && entity.type === type;

const addRelationshipData = (
  sourceEntity: Entity,
  targetEntity: Entity,
  relationship: Relationship,
  relationshipsDataMap: RelationshipsDataMap,
): void => {
  const { id } = sourceEntity;
  const { domain, type, id: entityId } = targetEntity;

  if (!relationshipsDataMap[id]) {
    relationshipsDataMap[id] = [];
  }

  const arr = relationshipsDataMap[id];
  // eslint-disable-next-line better-mutation/no-mutating-methods -- local accumulator array built within this function scope
  arr.push({
    relationshipId: relationship.id,
    domain,
    type,
    entityId,
  });
};

const initRelationshipDataBySource = (
  domain: string,
  type: string,
  id: string,
  relationshipDataBySource: RelationshipDataBySource,
): void => {
  if (!relationshipDataBySource) {
    throw new Error('failed to init relationship data by source');
  }

  if (!relationshipDataBySource[domain]) {
    relationshipDataBySource[domain] = {};
  }

  if (!relationshipDataBySource[domain][type]) {
    relationshipDataBySource[domain][type] = {};
  }

  if (!relationshipDataBySource[domain][type][id]) {
    relationshipDataBySource[domain][type][id] = { relationshipsData: [] };
  }

  if (!relationshipDataBySource[domain][type][id].relationshipsData) {
    relationshipDataBySource[domain][type][id].relationshipsData = [];
  }
};

const getRelationshipsDataMap = (
  relationships: Relationship[],
  domain: string,
  type: string,
): RelationshipsDataMap => {
  const relationshipsDataMap: RelationshipsDataMap = {};

  relationships.forEach((relationship) => {
    // We currently want to handle only 1 to 1 links.
    if (relationship.entities.length === 2) {
      const [first, second] = relationship.entities;
      if (isRelevantEntity(first, domain, type)) {
        addRelationshipData(first, second, relationship, relationshipsDataMap);
      }
      if (isRelevantEntity(second, domain, type)) {
        addRelationshipData(second, first, relationship, relationshipsDataMap);
      }
    }
  });

  return relationshipsDataMap;
};

export const addRelationshipsToState = (
  relationships: Relationship[],
  domain: string,
  type: string,
  nextState: {
    relationshipsEntityMap: RelationshipsEntityMap;
    relationshipDataBySource: RelationshipDataBySource;
  },
  addFirst?: boolean,
): void => {
  const relationshipDataById = getRelationshipsDataMap(
    relationships,
    domain,
    type,
  );

  relationships.forEach((relationship) => {
    nextState.relationshipsEntityMap[relationship.id] = relationship;
  });

  Object.keys(relationshipDataById).forEach((sourceId) => {
    initRelationshipDataBySource(
      domain,
      type,
      sourceId,
      nextState.relationshipDataBySource,
    );

    const nextStateRelationshipDataBySource =
      nextState.relationshipDataBySource[domain][type][sourceId];

    const existingRelationshipData =
      nextStateRelationshipDataBySource.relationshipsData;

    const getRelationshipId = (source: { relationshipId: string }) =>
      source.relationshipId;
    const currentRelationshipIds =
      existingRelationshipData.map(getRelationshipId);
    const newRelationshipIds =
      relationshipDataById[sourceId].map(getRelationshipId);

    const idsInBothCurrentAndNew = currentRelationshipIds.filter(
      (currentRelationshipId) =>
        newRelationshipIds.includes(currentRelationshipId),
    );

    const currentRelationshipData = existingRelationshipData.map(
      (relationship) =>
        idsInBothCurrentAndNew.includes(relationship.relationshipId)
          ? relationshipDataById[sourceId].find(
              ({ relationshipId }) =>
                relationshipId === relationship.relationshipId,
            ) || relationship
          : relationship,
    );

    const newRelationshipData = relationshipDataById[sourceId].filter(
      (relationship) =>
        !idsInBothCurrentAndNew.includes(relationship.relationshipId),
    );

    nextStateRelationshipDataBySource.relationshipsData = addFirst
      ? [...newRelationshipData, ...currentRelationshipData]
      : [...currentRelationshipData, ...newRelationshipData];
  });
};
